// Free Label Maker — type anything, print N of it, on the label stock the shop
// already has loaded.
//
// This page owns no format. Every dimension, font and die-cut offset comes from
// printing-utils, the same module behind Batch Label Print, drone stickers and
// receipt-box stickers, so a label typed here comes off the roll identical to
// one the fleet printed. If a label needs to change shape, it changes there and
// changes everywhere at once.
//
// The character caps are the fit guarantee, not the preview. Both are computed
// from Roboto Mono's 0.6em advance width and exported by printing-utils, so the
// number the counter enforces is the number the printer assumes.

import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { useToast } from '../components/Toast/ToastContext';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Radio, RadioGroup } from '../components/ui/Radio';
import Panel from '../components/ui/Panel';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import {
  printFreeTextLabels,
  printFreeQRLabels,
  wrapForSquare,
  FREE_TEXT_MAX_CHARS,
  MAX_FREE_LABELS,
} from '../utils/printing-utils';

type QrLayout = 'text' | 'pair';

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

/**
 * The print window pulls Roboto Mono from Google Fonts; the app itself never
 * loads it. Without this the preview renders in whatever monospace the OS
 * supplies, and a narrower fallback (Consolas is 0.55em against Roboto Mono's
 * 0.60em) would show text fitting that will not fit on the sticker.
 *
 * Injected here rather than in index.html because this is the only page that
 * needs it. If the fetch fails the preview degrades; the character caps still
 * hold, because they are arithmetic, not measurement.
 */
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap';
function useRobotoMono() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

const LabelMaker: React.FC = () => {
  const toast = useToast();
  useRobotoMono();

  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [qrLayout, setQrLayout] = useState<QrLayout>('text');
  const [qrPreview, setQrPreview] = useState('');

  const top = line1.trim();
  const bottom = line2.trim();
  const hasText = Boolean(top || bottom);

  // What the QR encodes: both lines joined, so the sticker scans as the whole
  // thing it reads as. Shown back verbatim below the preview — a QR is the one
  // control on this page whose contents nobody can check by looking.
  const qrPayload = useMemo(() => [top, bottom].filter(Boolean).join(' '), [top, bottom]);

  // Same wrap the printer runs. A preview that disagrees is worse than none.
  const caption = useMemo(() => wrapForSquare(qrPayload), [qrPayload]);

  const count = Math.max(1, Math.min(MAX_FREE_LABELS, Math.floor(Number(quantity) || 1)));
  const quantityValid = /^\d+$/.test(quantity.trim()) && count === Number(quantity.trim());

  useEffect(() => {
    let live = true;
    if (!qrPayload) {
      setQrPreview('');
      return;
    }
    QRCode.toDataURL(qrPayload, { width: 200, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (live) setQrPreview(url);
      })
      .catch(() => {
        if (live) setQrPreview('');
      });
    return () => {
      live = false;
    };
  }, [qrPayload]);

  const guard = (): boolean => {
    if (!hasText) {
      toast.error('Type something for the label to say.');
      return false;
    }
    if (!quantityValid) {
      toast.error(`Quantity has to be a whole number, 1 to ${MAX_FREE_LABELS}.`);
      return false;
    }
    return true;
  };

  const printText = () => {
    if (!guard()) return;
    printFreeTextLabels({ line1: top, line2: bottom, quantity: count });
  };

  const printQR = async () => {
    if (!guard()) return;
    await printFreeQRLabels({ text: qrPayload, quantity: count, mode: qrLayout });
  };

  const printBoth = async () => {
    if (!guard()) return;
    printFreeTextLabels({ line1: top, line2: bottom, quantity: count });
    await printFreeQRLabels({ text: qrPayload, quantity: count, mode: qrLayout });
  };

  const counter = (value: string) => (
    <span
      className={`text-[11px] tabular-nums ${
        value.length >= FREE_TEXT_MAX_CHARS ? 'text-yellow-400' : 'text-on-surface-variant'
      }`}
    >
      {value.length}/{FREE_TEXT_MAX_CHARS}
    </span>
  );

  return (
    <PageLayout
      kind="focus"
      className="pt-6 text-on-surface"
      header={
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Free Label Maker</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Type anything, print as many as you need. Same label stock, same sizes and same printer
          settings as the drone stickers, so nothing needs reloading.
        </p>
      </header>
      }
    >

      <Panel title="What it says" titleStyle="section" className="mb-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className={`${labelClass} mb-0`} htmlFor="label-line1">
                Line 1
              </label>
              {counter(line1)}
            </div>
            <Field
              id="label-line1"
              value={line1}
              maxLength={FREE_TEXT_MAX_CHARS}
              autoFocus
              placeholder="SHOP SPARES"
              onChange={(e) => setLine1(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className={`${labelClass} mb-0`} htmlFor="label-line2">
                Line 2 <span className="normal-case tracking-normal text-on-surface-variant">(optional)</span>
              </label>
              {counter(line2)}
            </div>
            <Field
              id="label-line2"
              value={line2}
              maxLength={FREE_TEXT_MAX_CHARS}
              placeholder="SHELF B4"
              onChange={(e) => setLine2(e.target.value)}
            />
          </div>

          <div className="w-32">
            <label className={labelClass} htmlFor="label-qty">
              Quantity
            </label>
            <Field
              id="label-qty"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <Reveal show={!quantityValid && quantity.trim() !== ''}>
            <Alert tone="warning" variant="text">
              Whole numbers from 1 to {MAX_FREE_LABELS}.
            </Alert>
          </Reveal>
        </div>
      </Panel>

      <Panel
        title="Preview"
        titleStyle="section"
        className="mb-6"
        actions={<span className="text-xs text-on-surface-variant">shown 2x actual size</span>}
      >
        <div>
          {!hasText ? (
            <FadeIn key="empty"><EmptyState variant="line">Nothing typed yet. The preview shows up here.</EmptyState></FadeIn>
          ) : (
            <FadeIn key="preview" className="flex flex-wrap gap-10">
              {/* Wide label. Print units (in/mm) on purpose: the browser maps
                  them the same way on screen, so the box is the real box. */}
              <div>
                <div className={labelClass}>Wide label &middot; 1.5in x 0.375in</div>
                <div style={{ width: '3in', height: '0.75in' }}>
                  <div
                    style={{
                      transform: 'scale(2)',
                      transformOrigin: 'top left',
                      width: '1.5in',
                      height: '0.375in',
                      background: '#fff',
                      color: '#000',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontFamily: "'Roboto Mono', monospace",
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {top && <div style={{ width: '100%', textAlign: 'center' }}>{top}</div>}
                    {bottom && <div style={{ width: '100%', textAlign: 'center' }}>{bottom}</div>}
                  </div>
                </div>
              </div>

              {/* QR label. Both layouts share the 28.5mm x 11.4mm row. */}
              <div>
                <div className={labelClass}>QR label &middot; 28.5mm x 11.4mm</div>
                <div style={{ width: '57mm', height: '22.8mm' }}>
                  <div
                    style={{
                      transform: 'scale(2)',
                      transformOrigin: 'top left',
                      width: '28.5mm',
                      height: '11.4mm',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3mm',
                    }}
                  >
                    {qrPreview ? (
                      <img
                        src={qrPreview}
                        alt="QR preview"
                        style={{ width: '11.4mm', height: '11.4mm', display: 'block' }}
                      />
                    ) : (
                      <div style={{ width: '11.4mm', height: '11.4mm' }} />
                    )}

                    {qrLayout === 'pair' ? (
                      qrPreview && (
                        <img
                          src={qrPreview}
                          alt="QR preview"
                          style={{ width: '11.4mm', height: '11.4mm', display: 'block' }}
                        />
                      )
                    ) : (
                      <div
                        style={{
                          width: '11.4mm',
                          height: '11.4mm',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: '#000',
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: '7px',
                          fontWeight: 'bold',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                        }}
                      >
                        {caption.lines.map((line, i) => (
                          <div key={i} style={{ textAlign: 'center' }}>
                            {line}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <RadioGroup
                  label="QR layout"
                  hideLabel
                  name="qr-layout"
                  layout="row"
                  className="mt-2 text-sm"
                  value={qrLayout}
                  onChange={(v) => setQrLayout(v)}
                >
                  <Radio value="text" label="Text beside QR" />
                  <Radio value="pair" label="Two QR per row" />
                </RadioGroup>

                <div className="mt-3 text-xs text-on-surface-variant">
                  QR scans as{' '}
                  <code className="font-mono text-on-surface break-all">{qrPayload}</code>
                </div>

                <Reveal show={qrLayout === 'text' && caption.overflow}>
                  <Alert tone="warning" variant="note" className="mt-2 max-w-xs">
                    Too long for the square beside the QR, so the printed text stops early. The QR
                    still carries all of it. Shorten it, or switch to two QR per row.
                  </Alert>
                </Reveal>
              </div>
            </FadeIn>
          )}
        </div>
      </Panel>

      {/* Same three modes as Batch Label Print. */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={printText}
          disabled={!hasText}
          className={buttonClass('filled')}
        >
          Labels
        </button>
        <button
          type="button"
          onClick={printQR}
          disabled={!hasText}
          className={buttonClass('info')}
        >
          QR
        </button>
        <button
          type="button"
          onClick={printBoth}
          disabled={!hasText}
          className={buttonClass('good')}
        >
          Both
        </button>
        <span className="text-sm text-on-surface-variant">
          {hasText && quantityValid
            ? `${count} label${count === 1 ? '' : 's'} per print`
            : 'Both opens two print windows.'}
        </span>
      </div>
    </PageLayout>
  );
};

export default LabelMaker;
